import type { ThemeClasses, ViewType } from "../types";
import type { TFunction } from "i18next";
import Select from "react-select";
import { getCustomSelectStyles, FilterPopover } from "../../../../shared";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";

interface TrailerFiltersProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedFilter: string;
  selectedType: string;
  searchQuery: string;
  onFilterChange: (filter: string) => void;
  onTypeChange: (type: string) => void;
  onSearchChange: (search: string) => void;
  onViewChange: (view: ViewType) => void;
  selectedView: ViewType;
  t: TFunction;
}

export function TrailerFilters({
  themeClasses,
  isDarkMode,
  selectedFilter,
  selectedType,
  searchQuery,
  onFilterChange,
  onTypeChange,
  onSearchChange,
  onViewChange,
  selectedView,
  t,
}: TrailerFiltersProps) {
  // Opciones de tipo de remolque
  const typeOptions = [
    { value: "dry_van", label: t("trailers.filterType.dry_van") },
    { value: "refrigerated", label: t("trailers.filterType.refrigerated") },
    { value: "flatbed", label: t("trailers.filterType.flatbed") },
    { value: "tank", label: t("trailers.filterType.tank") },
    { value: "lowboy", label: t("trailers.filterType.lowboy") },
    { value: "container", label: t("trailers.filterType.container") },
    { value: "dump", label: t("trailers.filterType.dump") },
    { value: "car_carrier", label: t("trailers.filterType.car_carrier") },
  ];

  // Opciones de estado desde el store
  const statuses = useStatusesStore((state) => state.statuses);
  const statusOptionsSelect = getStatusOptionsForEntity(statuses, "trailers", t);

  return (
    <div
      className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 sm:px-6 pb-3`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* Búsqueda */}
        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
          <i
            className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
          ></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("trailers.searchPlaceholder")}
            className={`w-full h-9 pl-10 py-2 ${
              isDarkMode ? "bg-dark-card" : "bg-gray-50"
            } border ${themeClasses.cardBorder} rounded-lg ${
              themeClasses.text
            } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm`}
          />
        </div>

        <FilterPopover
          isDarkMode={isDarkMode}
          activeCount={(selectedType ? 1 : 0) + (selectedFilter ? 1 : 0)}
          onClear={() => {
            onTypeChange("");
            onFilterChange("");
          }}
        >
          {/* Tipo de remolque */}
          <Select
            options={typeOptions}
            styles={getCustomSelectStyles(isDarkMode)}
            placeholder={t("trailers.filterType")}
            value={typeOptions.find((opt) => opt.value === selectedType) || null}
            onChange={(option: any) => onTypeChange(option?.value || "")}
            isSearchable
            isClearable
            noOptionsMessage={() => t("trailers.filters.noTypesAvailable")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />

          {/* Estado */}
          <Select
            options={statusOptionsSelect}
            styles={getCustomSelectStyles(isDarkMode)}
            placeholder={t("trailers.filterStatus")}
            value={statusOptionsSelect.find((opt) => opt.value === selectedFilter) || null}
            onChange={(option: any) => onFilterChange(option?.value || "")}
            isSearchable
            isClearable
            noOptionsMessage={() => t("trailers.filters.noStatusesAvailable")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </FilterPopover>

        <div className="ml-auto flex shrink-0">
          <div
            className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
          >
            <button
              className={`px-3 py-1 rounded-md transition-colors ${
                selectedView === "grid"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
              }`}
              onClick={() => onViewChange("grid")}
            >
              <i className="ri-grid-fill"></i>
            </button>
            <button
              className={`px-3 py-1 rounded-md transition-colors ${
                selectedView === "list"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
              }`}
              onClick={() => onViewChange("list")}
            >
              <i className="ri-list-check"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
